import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import { makeStyles } from "@material-ui/core/styles";
import TableCell from "@material-ui/core/TableCell";
import React from "react";
import capitalizeString from "../../../functions/capitalizeString.function";

const useStyles = makeStyles({
  tableCell: {
    border: "1px solid",
    borderColor: "#ECECED",
    verticalAlign: "top",
  },
  listElement: {
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "0px",

    "&:hover": {
      // forced to use !important bc used in theme override
      backgroundColor: "inherit !important",
    },
  },
  cellHeadline: {
    color: "#757575",
    fontSize: "0.9rem",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  noPadding: {
    paddingTop: "0px",
    paddingBottom: "0px",
  },
});

export type ClickTarget<T> = {
  value: T;
  name: string;
  index: number;
};

function formatTitle(title: string, isMandatory: boolean) {
  return capitalizeString(title) + (isMandatory ? "*" : "");
}

interface TableListCellProps<LE, LC> {
  component: LC;
  list: LE[];
  mandatory?: boolean;
  onElementClick?: (target: ClickTarget<LE>) => void;
  onElementDelete?: (target: ClickTarget<LE>) => void;
  name: string;
  title?: string;
}

function TableListCell<
  ListElement extends { name: string; id: number },
  ListComponent extends <
    LCP extends {
      clickable: boolean;
      color: "primary" | "secondary";
      label: string;
      onClick: () => void;
      onDelete: () => void;
    },
  >(
    props: LCP,
  ) => JSX.Element,
>({
  component,
  onElementClick: handleElementClick,
  onElementDelete: handleElementDelete,
  mandatory,
  name,
  list,
  title,
}: TableListCellProps<ListElement, ListComponent>) {
  const classes = useStyles();

  return (
    <TableCell align="left" className={classes.tableCell}>
      <div className={classes.cellHeadline}>{formatTitle(title || name, !!mandatory)}</div>
      <List className={classes.noPadding}>
        {list.map((elm, index) => (
          <ListItem key={elm.id} className={classes.listElement}>
            {React.createElement(component, {
              clickable: !!handleElementClick,
              color: "primary",
              label: elm.name,
              onClick: () => handleElementClick?.({ value: elm, name, index }),
              onDelete: () => handleElementDelete?.({ value: elm, name, index }),
            })}
          </ListItem>
        ))}
      </List>
    </TableCell>
  );
}

export default TableListCell;
